import React, { useMemo, useState } from "react";
import { arrayUnion, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { PDFDocument } from "pdf-lib";
import { auth, db, storage } from "../../../firebaseConfig";
import {
  cleanText,
  collectProjectDocumentRecords,
  findProjectDocument,
  getPacsIdentity,
  safeFilePart,
} from "./pacsWorkflowUtils";
import {
  buildPackageIndexHtml,
  buildRegistrarApplicationHtml,
} from "./pacsOfficialTemplates";
import {
  officialHtmlToPdfBlob,
  openOfficialPreview,
} from "./officialPdf";
import { PACS_DOCUMENT_TYPES } from "./generatePacsDocuments";
import {
  PACS_FOUNDATION_DOCUMENTS,
  REQUIRED_SIGNED_PACS_DOCUMENT_COUNT,
  technicalDocumentRecord,
} from "./pacsAutomationConfig";
import {
  createZipBlob,
  downloadBlob,
  fetchRemoteBlob,
} from "./simpleZip";

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const REQUIRED_PACS_DOCUMENT_COUNT =
  Number(REQUIRED_SIGNED_PACS_DOCUMENT_COUNT) || 5;

const GROUPS = [
  ...PACS_FOUNDATION_DOCUMENTS,
  {
    id: "RESOLUTIONS",
    title: "One Combined PDF — Five Official PACS Documents",
    terms: ["RESOLUTION", "COMMITTEE_RESOLUTION", "MARGIN_MONEY"],
  },
  {
    id: "LAND_LEASE",
    title: "Land Document + Registered Lease (for Lease Land)",
    terms: ["SIGNED_LEASE", "LAND_RECORD", "LAND"],
  },
  {
    id: "PMC_FINAL_DPR",
    title: "PMC Final Detailed Project Report (DPR)",
    terms: ["DPR", "FINAL_DPR", "DETAILED PROJECT REPORT"],
    workflowManaged: true,
    downloadLabel: "Download DPR",
  },
  {
    id: "SIGNED_MOU",
    title: "Fully Executed PACS–PMC MOU",
    terms: ["MOU", "SIGNED_MOU", "FINAL_MOU", "EXECUTED_MOU"],
    workflowManaged: true,
    downloadLabel: "Download Signed MOU",
  },
  {
    id: "NAFED_ASSURANCE_LETTER",
    title: "NAFED Assurance Letter",
    terms: ["NAFED_ASSURANCE_LETTER", "NAFED ASSURANCE", "ASSURANCE LETTER"],
  },
];

const cleanFileName = (value) => String(value || "document").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 120);

const officialPacsDocuments = (project = {}) =>
  PACS_DOCUMENT_TYPES.map(
    (item) =>
      project.generatedDocuments?.[item.id] ||
      project.signedPacsDocuments?.[item.id],
  ).filter((item) => item?.url);

const finalDprRecord = (project = {}) => {
  const finalUrl = project.finalSignedDprUrl || project.finalDprUrl || "";
  const publishedUrl =
    project.dprOriginalPdfUrl || project.dprPdfUrl || project.dprUrl || "";
  const url = finalUrl || publishedUrl;
  if (!url) return null;
  return {
    code: "PMC_FINAL_DPR",
    type: finalUrl ? "PACS_SIGNED_FINAL_DPR" : "PMC_PUBLISHED_DPR",
    title: finalUrl
      ? "Final DPR signed and accepted by PACS"
      : "PMC-signed final DPR published to PACS",
    fileName:
      project.finalSignedDprFileName ||
      project.dprFileName ||
      `Final_DPR_${safeFilePart(project.applicationId || project.projectId || project.id)}.pdf`,
    storagePath:
      project.finalSignedDprStoragePath ||
      project.dprOriginalStoragePath ||
      "",
    url,
    version: project.dprVersion || "V001",
    status: finalUrl ? "PACS_SIGNED_FINAL" : project.dprStatus || "PMC_PUBLISHED",
    linkedFromWorkflow: true,
  };
};

const fullySignedMouRecord = (project = {}) => {
  const url =
    project.finalMouUrl ||
    project.finalSignedMouUrl ||
    project.mouFinalPdfUrl ||
    project.pacsSignedMouUrl ||
    project.mouSignatures?.pacs?.signedPdfUrl ||
    "";
  if (!url) return null;
  return {
    code: "SIGNED_MOU",
    type: "FULLY_EXECUTED_PACS_PMC_MOU",
    title: "Fully signed PACS–PMC Memorandum of Understanding",
    fileName:
      project.mouFinalPdfFileName ||
      project.finalMouFileName ||
      `Signed_MOU_${safeFilePart(project.applicationId || project.projectId || project.id)}.pdf`,
    storagePath:
      project.mouFinalPdfStoragePath ||
      project.finalSignedMouStoragePath ||
      project.pacsSignedMouStoragePath ||
      "",
    url,
    version: project.mouVersion || "V1",
    status: project.mouStatus || project.mouStage || "FULLY_EXECUTED",
    linkedFromWorkflow: true,
  };
};

const mergeOfficialPdfRecords = async (records = []) => {
  if (records.length !== REQUIRED_PACS_DOCUMENT_COUNT) {
    throw new Error(`All ${REQUIRED_PACS_DOCUMENT_COUNT} official PACS PDFs are required before creating the combined file.`);
  }
  const combined = await PDFDocument.create();
  for (let index = 0; index < records.length; index += 1) {
    const record = records[index];
    const sourceBlob = await fetchRemoteBlob(
      record.url,
      record.fileName || `${String(index + 1).padStart(2, "0")}_Official_PACS_Document.pdf`,
    );
    const sourcePdf = await PDFDocument.load(await sourceBlob.arrayBuffer());
    const copiedPages = await combined.copyPages(
      sourcePdf,
      sourcePdf.getPageIndices(),
    );
    copiedPages.forEach((page) => combined.addPage(page));
  }
  return new Blob([await combined.save()], { type: "application/pdf" });
};

export default function PacsRegistrarFileCenter({
  profile = {},
  project = {},
  onSaved,
}) {
  const identity = useMemo(() => getPacsIdentity(profile, project), [profile, project]);
  const records = useMemo(() => collectProjectDocumentRecords(project), [project]);
  const [customDocuments, setCustomDocuments] = useState(project.registrarFileDocuments || {});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const groupState = useMemo(
    () =>
      GROUPS.map((group) => {
        const custom = customDocuments[group.id];
        const customDeleted = custom?.status === "DELETED";
        const linked = findProjectDocument(records, ...group.terms);
        const generatedOfficialDocuments = officialPacsDocuments(project);
        const resolutionCount = generatedOfficialDocuments.length;
        const signedLeaseUrl = project.landLeaseDocuments?.SIGNED_LEASE?.url;
        const landDocumentUrl = project.landLeaseDocuments?.LAND_RECORD?.url;
        const leaseMode = cleanText(identity.landOwnershipType).toUpperCase().includes("LEASE");
        const landDocumentsReady = leaseMode
          ? Boolean(signedLeaseUrl && landDocumentUrl)
          : Boolean(landDocumentUrl);
        const dprRecord =
          group.id === "PMC_FINAL_DPR" ? finalDprRecord(project) : null;
        const mouRecord =
          group.id === "SIGNED_MOU" ? fullySignedMouRecord(project) : null;
        const allowedLinked = [
          "LAND_LEASE",
          "PMC_FINAL_DPR",
          "SIGNED_MOU",
        ].includes(group.id)
          ? null
          : linked;
        const special =
          group.id === "RESOLUTIONS" &&
          resolutionCount === REQUIRED_PACS_DOCUMENT_COUNT
            ? {
                title: "All five system-generated signed-and-stamped PACS PDFs",
                url: "MULTIPLE_RESOLUTIONS",
              }
            : group.id === "LAND_LEASE" && landDocumentsReady
              ? {
                  title: leaseMode
                    ? "Executed registered lease agreement and land document"
                    : "Land document",
                  url: signedLeaseUrl || landDocumentUrl,
                }
              : group.id === "PMC_FINAL_DPR"
                ? dprRecord
                : group.id === "SIGNED_MOU"
                  ? mouRecord
                  : null;
        const customAllowed =
          !group.workflowManaged && !customDeleted && custom?.url;
        const resolvedRecord = customAllowed
          ? custom
          : customDeleted
            ? null
            : special || allowedLinked;
        return {
          ...group,
          record: resolvedRecord,
          available: Boolean(resolvedRecord?.url),
          deleted: customDeleted,
          statusNote:
            customDeleted
              ? "Removed from Registrar File — upload/rebuild to restore"
              : group.id === "RESOLUTIONS" &&
            resolutionCount < REQUIRED_PACS_DOCUMENT_COUNT
              ? `${resolutionCount}/${REQUIRED_PACS_DOCUMENT_COUNT} official PDFs generated`
              : group.id === "RESOLUTIONS" && !custom?.url
                ? "Five official PDFs are ready — create the one combined PDF"
              : group.id === "PMC_FINAL_DPR" && !dprRecord
                ? "Waiting for the PMC-published DPR"
                : group.id === "SIGNED_MOU" && !mouRecord
                  ? "Waiting for the fully signed PACS–PMC MOU"
                  : "",
        };
      }),
    [customDocuments, identity.landOwnershipType, project, records],
  );

  const missingRequired = groupState.filter((item) => !item.optional && !item.available);
  const packageReady = Boolean(project.id) && missingRequired.length === 0;

  const show = (text, type = "info") => setMessage({ text, type });

  const uploadSelected = async () => {
    const user = auth.currentUser;
    if (!user || !project.id) return show("Application/login is unavailable.", "error");
    const selected = Object.entries(selectedFiles).filter(([, file]) => file);
    if (!selected.length) return show("Select at least one missing document.", "error");
    try {
      setBusy("upload");
      const next = { ...customDocuments };
      for (const [groupId, file] of selected) {
        if (file.size > MAX_FILE_SIZE) throw new Error(`${file.name} exceeds the 20 MB limit.`);
        const version = Number(next[groupId]?.version || 0) + 1;
        const storagePath = `nafed_pacs/${user.uid}/${project.id}/registrar_file/source/${groupId}/v${version}_${Date.now()}_${cleanFileName(file.name)}`;
        const objectRef = ref(storage, storagePath);
        await uploadBytes(objectRef, file, {
          contentType: file.type || "application/octet-stream",
          customMetadata: { projectId: project.id, documentGroup: groupId, uploadedByUid: user.uid },
        });
        next[groupId] = {
          code: groupId,
          title: GROUPS.find((item) => item.id === groupId)?.title || groupId,
          fileName: file.name,
          storagePath,
          url: await getDownloadURL(objectRef),
          version,
          status: "UPLOADED",
          uploadedAtIso: new Date().toISOString(),
          uploadedByUid: user.uid,
        };
      }
      const update = { registrarFileDocuments: next, registrarFileStage: "SOURCE_DOCUMENTS_UPDATED", updatedAt: serverTimestamp() };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      setCustomDocuments(next);
      setSelectedFiles({});
      onSaved?.(update);
      show("Registrar source documents uploaded successfully.", "success");
    } catch (error) {
      console.error(error);
      show(error.message || "Registrar documents could not be uploaded.", "error");
    } finally {
      setBusy("");
    }
  };

  const createCombinedOfficialPdf = async () => {
    const user = auth.currentUser;
    if (!user || !project.id) return show("Application/login is unavailable.", "error");
    const officialDocuments = officialPacsDocuments(project);
    if (officialDocuments.length !== REQUIRED_PACS_DOCUMENT_COUNT) {
      return show(
        `Generate all ${REQUIRED_PACS_DOCUMENT_COUNT} official PACS documents first.`,
        "error",
      );
    }
    const previewWindow = window.open("about:blank", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = "Preparing combined PACS PDF";
      previewWindow.document.body.textContent = "Preparing the combined five-document PDF...";
    }
    try {
      setBusy("combine");
      const combinedPdf = await mergeOfficialPdfRecords(officialDocuments);
      const previous = customDocuments.RESOLUTIONS;
      const version = Number(previous?.version || 0) + 1;
      const fileName = `Five_Official_PACS_Documents_Combined_${safeFilePart(identity.applicationId)}_V${version}.pdf`;
      const storagePath = `nafed_pacs/${user.uid}/${project.id}/registrar_file/source/RESOLUTIONS/v${version}_${Date.now()}_${cleanFileName(fileName)}`;
      const objectRef = ref(storage, storagePath);
      await uploadBytes(objectRef, combinedPdf, {
        contentType: "application/pdf",
        customMetadata: {
          projectId: project.id,
          documentGroup: "RESOLUTIONS",
          mergedDocumentCount: String(REQUIRED_PACS_DOCUMENT_COUNT),
          uploadedByUid: user.uid,
        },
      });
      const combinedUrl = await getDownloadURL(objectRef);
      const record = {
        code: "RESOLUTIONS",
        type: "COMBINED_OFFICIAL_PACS_DOCUMENTS",
        title: "One combined PDF containing all five official PACS documents",
        fileName,
        storagePath,
        url: combinedUrl,
        version,
        fileCount: REQUIRED_PACS_DOCUMENT_COUNT,
        sourceDocuments: officialDocuments.map((item) => ({
          type: item.type || item.id || "OFFICIAL_PACS_DOCUMENT",
          version: item.version || 1,
          url: item.url,
        })),
        status: "UPLOADED",
        uploadedAtIso: new Date().toISOString(),
        uploadedByUid: user.uid,
      };
      const next = { ...customDocuments, RESOLUTIONS: record };
      const update = {
        registrarFileDocuments: next,
        registrarFileStage: "SOURCE_DOCUMENTS_UPDATED",
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      setCustomDocuments(next);
      onSaved?.(update);
      if (previewWindow) previewWindow.location.replace(combinedUrl);
      show("All five official PACS PDFs were merged and saved as one viewable PDF.", "success");
    } catch (error) {
      if (previewWindow) previewWindow.close();
      console.error(error);
      show(error.message || "The five official PDFs could not be combined.", "error");
    } finally {
      setBusy("");
    }
  };

  const deleteRegistrarDocument = async (group) => {
    const user = auth.currentUser;
    if (!user || !project.id) return show("Application/login is unavailable.", "error");
    const confirmed = window.confirm(
      `Delete “${group.title}” from this Registrar File? The earlier source/version will remain preserved in audit history.`,
    );
    if (!confirmed) return;
    try {
      setBusy(`delete:${group.id}`);
      const previous = customDocuments[group.id] || group.record || {};
      const next = {
        ...customDocuments,
        [group.id]: {
          code: group.id,
          title: group.title,
          status: "DELETED",
          version: Number(previous.version || 0) + 1,
          previousFileName: previous.fileName || previous.title || "",
          previousStoragePath: previous.storagePath || "",
          deletedAtIso: new Date().toISOString(),
          deletedByUid: user.uid,
        },
      };
      const update = {
        registrarFileDocuments: next,
        registrarFileStage: "SOURCE_DOCUMENTS_UPDATED",
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      setCustomDocuments(next);
      setSelectedFiles((old) => ({ ...old, [group.id]: null }));
      onSaved?.(update);
      show(`${group.title} deleted from the active Registrar File.`, "success");
    } catch (error) {
      console.error(error);
      show(error.message || "The document could not be deleted.", "error");
    } finally {
      setBusy("");
    }
  };

  const appendRemote = async (entries, folder, record, fallbackName) => {
    if (!record?.url || record.url === "MULTIPLE_RESOLUTIONS") return;
    const blob = await fetchRemoteBlob(record.url, fallbackName);
    entries.push({ name: `${folder}/${cleanFileName(record.fileName || fallbackName)}`, data: blob });
  };

  const downloadWorkflowDocument = async (group) => {
    if (!group?.record?.url) return;
    try {
      setBusy(`download:${group.id}`);
      const fallbackName =
        group.id === "PMC_FINAL_DPR"
          ? `Final_DPR_${safeFilePart(identity.applicationId)}.pdf`
          : `Signed_MOU_${safeFilePart(identity.applicationId)}.pdf`;
      const fileName = cleanFileName(group.record.fileName || fallbackName);
      const blob = await fetchRemoteBlob(group.record.url, fileName);
      downloadBlob(blob, fileName);
      show(`${group.title} downloaded successfully.`, "success");
    } catch (error) {
      console.error(error);
      show(error.message || `${group.title} could not be downloaded.`, "error");
    } finally {
      setBusy("");
    }
  };

  const generatePackage = async () => {
    if (!packageReady) return show(`Complete required documents: ${missingRequired.map((item) => item.title).join(" · ")}`, "error");
    const user = auth.currentUser;
    if (!user) return show("Login session has expired.", "error");
    try {
      setBusy("package");
      const indexHtml = buildPackageIndexHtml({
        title: "District Registrar Project File",
        project: identity,
        items: groupState,
        notes: ["Only the latest available document version is included.", "Draft/deleted records are excluded from this package."],
      });
      const applicationHtml = buildRegistrarApplicationHtml(identity, "BILINGUAL");
      const entries = [
        {
          name: "00_Index_and_Checklist.pdf",
          data: await officialHtmlToPdfBlob(
            indexHtml,
            "District_Registrar_Index.pdf",
          ),
        },
        {
          name: "01_Bilingual_Application_and_Undertaking.pdf",
          data: await officialHtmlToPdfBlob(
            applicationHtml,
            "District_Registrar_Application.pdf",
          ),
        },
      ];

      for (let index = 0; index < groupState.length; index += 1) {
        const group = groupState[index];
        if (!group.available) continue;
        const folder = `${String(index + 2).padStart(2, "0")}_${safeFilePart(group.id)}`;
        if (group.id === "RESOLUTIONS") {
          if (group.record?.url && group.record.url !== "MULTIPLE_RESOLUTIONS") {
            await appendRemote(
              entries,
              folder,
              group.record,
              "Five_Official_PACS_Documents_Combined.pdf",
            );
          } else {
            entries.push({
              name: `${folder}/Five_Official_PACS_Documents_Combined.pdf`,
              data: await mergeOfficialPdfRecords(officialPacsDocuments(project)),
            });
          }
        } else if (group.id === "LAND_LEASE") {
          const customLand = customDocuments.LAND_LEASE;
          if (customLand?.url && customLand.status !== "DELETED") {
            await appendRemote(entries, folder, customLand, "Land_and_Registered_Lease.pdf");
          } else {
            const landRecords = ["SIGNED_LEASE", "LAND_RECORD"]
              .map((type) => project.landLeaseDocuments?.[type])
              .filter((item) => item?.url);
            if (landRecords.length) {
              for (const item of landRecords)
                await appendRemote(
                  entries,
                  folder,
                  item,
                  item.fileName || `${item.type}.pdf`,
                );
            } else {
              await appendRemote(entries, folder, group.record, "Land_or_Lease.pdf");
            }
          }
        } else {
          await appendRemote(entries, folder, group.record, `${group.id}.pdf`);
        }
      }

      entries.push({
        name: "manifest.json",
        data: JSON.stringify({
          packageType: "DISTRICT_REGISTRAR_FILE",
          projectId: project.id,
          applicationId: identity.applicationId,
          pacsName: identity.societyName,
          generatedAtIso: new Date().toISOString(),
          files: entries.map((entry) => entry.name),
        }, null, 2),
      });

      const zip = await createZipBlob(entries);
      const version = Number(project.registrarPackageVersion || 0) + 1;
      const fileName = `District_Registrar_File_${safeFilePart(identity.district)}_${safeFilePart(identity.societyName)}_${safeFilePart(identity.applicationId)}_V${version}.zip`;
      const storagePath = `nafed_pacs/${user.uid}/${project.id}/registrar_file/packages/v${version}_${fileName}`;
      const objectRef = ref(storage, storagePath);
      await uploadBytes(objectRef, zip, { contentType: "application/zip", customMetadata: { projectId: project.id, packageVersion: String(version), generatedByUid: user.uid } });
      const record = {
        version,
        fileName,
        storagePath,
        url: await getDownloadURL(objectRef),
        status: "READY",
        fileCount: entries.length,
        generatedAtIso: new Date().toISOString(),
        generatedByUid: user.uid,
      };
      const snapshotDocuments = {};
      groupState.forEach((group) => {
        if (group.record?.url && group.record.url !== "MULTIPLE_RESOLUTIONS")
          snapshotDocuments[group.id] = group.record;
      });
      const generatedAtIso = record.generatedAtIso;
      const update = {
        registrarPackage: record,
        registrarPackageVersion: version,
        registrarFileStage: "READY_FOR_PMC_FORWARDING",
        workflowStage: "REGISTRAR_PACKAGE_READY_FOR_PMC",
        currentStage: "Registrar Package Ready for PMC Forwarding",
        physicalFileStatus: "COMPLETE_AND_DOWNLOADED",
        registrarReviewStatus: "PENDING_PMC_FORWARD",
        pmcRegistrarStatus: "WAITING_FOR_PMC_FORWARD",
        pmcRegistrarFileForwardedToDistrict: false,
        districtRegistrarVerified: false,
        districtPhysicalFileForwardedToState: false,
        registrarWorkflow: {
          pmc: { status: "WAITING_FOR_PMC_FORWARD" },
          district: { status: "WAITING_FOR_PMC_FORWARD" },
          state: { status: "WAITING_FOR_DISTRICT_FORWARD" },
          districtRegistrarStatus: "WAITING_FOR_PMC_FORWARD",
          stateRegistrarStatus: "WAITING_FOR_DISTRICT_REVIEW",
          packageVersion: version,
          submittedAtIso: generatedAtIso,
          submittedByUid: user.uid,
        },
        registrarDocumentSnapshot: {
          documents: snapshotDocuments,
          fiveOfficialPacsDocuments: officialPacsDocuments(project),
          combinedOfficialPacsDocument:
            customDocuments.RESOLUTIONS?.status !== "DELETED"
              ? customDocuments.RESOLUTIONS || null
              : null,
          landLeaseDocuments: project.landLeaseDocuments || {},
          pmcPreliminaryEstimate: technicalDocumentRecord(
            project,
            "PMC_PRELIMINARY_ESTIMATE",
          ),
          pmcFinalDpr:
            groupState.find((group) => group.id === "PMC_FINAL_DPR")?.record ||
            null,
          fullyExecutedMou:
            groupState.find((group) => group.id === "SIGNED_MOU")?.record ||
            null,
          package: record,
          generatedAtIso,
        },
        authorityVisibility: {
          ...(project.authorityVisibility || {}),
          PACS: true,
          PMC: true,
          STATE_BRANCH: true,
          DISTRICT_REGISTRAR: true,
          STATE_REGISTRAR: true,
          HEAD_OFFICE: true,
        },
        registrarPackageHistory: arrayUnion(record),
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      const auditResult = await Promise.allSettled([setDoc(doc(db, "nafed_activity", `${project.id}_REGISTRAR_PACKAGE_${Date.now()}`), {
        action: "PACS_DISTRICT_REGISTRAR_PACKAGE_GENERATED",
        entityType: "GODOWN_PROJECT",
        entityId: project.id,
        applicationId: identity.applicationId,
        packageVersion: version,
        fileCount: entries.length,
        performedByUid: user.uid,
        performedByRole: "PACS",
        createdAt: serverTimestamp(),
        createdAtIso: record.generatedAtIso,
      }),
        ...["PMC", "STATE_BRANCH"].map((role) =>
          setDoc(
            doc(
              db,
              "nafed_notifications",
              `${project.id}_REGISTRAR_READY_${role}`,
            ),
            {
              type: "REGISTRAR_PACKAGE_READY_FOR_PMC",
              title: "Registrar Package Ready for PMC Forwarding",
              message: `${identity.societyName} complete Registrar ZIP is ready. The allocated PMC must forward it to the District Registrar.`,
              projectId: project.id,
              applicationId: identity.applicationId,
              recipientRole: role,
              state: identity.state,
              district: identity.district,
              status: "UNREAD",
              createdAt: serverTimestamp(),
              createdAtIso: generatedAtIso,
            },
            { merge: true },
          ),
        )]);
      auditResult.forEach((result) => {
        if (result.status === "rejected")
          console.error("Registrar package secondary audit/notification failed", result.reason);
      });
      downloadBlob(zip, fileName);
      onSaved?.(update);
      show("Registrar ZIP generated. It is now waiting for the allocated PMC to forward it to District.", "success");
    } catch (error) {
      console.error(error);
      show(error.message || "District Registrar ZIP could not be generated.", "error");
    } finally {
      setBusy("");
    }
  };

  const previewApplication = () => {
    try { openOfficialPreview(buildRegistrarApplicationHtml(identity, "BILINGUAL")); }
    catch (error) { show(error.message, "error"); }
  };

  const lifecycleSteps = [
    {
      title: "Application Documents",
      done: PACS_FOUNDATION_DOCUMENTS.every(
        (item) => groupState.find((group) => group.id === item.id)?.available,
      ),
      note: "Registration, bye-laws, 3-year financials and audits",
    },
    {
      title: "Five PDFs — One File",
      done: Boolean(
        customDocuments.RESOLUTIONS?.url &&
          customDocuments.RESOLUTIONS?.status !== "DELETED",
      ),
      note: "All five official PACS PDFs merged into one viewable PDF",
    },
    {
      title: "Land / Registered Lease",
      done: Boolean(
        groupState.find((group) => group.id === "LAND_LEASE")?.available,
      ),
      note: "Owned-land record or land record plus registered lease",
    },
    {
      title: "DPR & Signed MOU",
      done: Boolean(
        groupState.find((group) => group.id === "PMC_FINAL_DPR")?.available &&
          groupState.find((group) => group.id === "SIGNED_MOU")?.available,
      ),
      note: "PMC DPR and executed PACS–PMC MOU linked automatically",
    },
    {
      title: "Registrar ZIP",
      done: Boolean(project.registrarPackage?.url),
      note: "Online file visible to District and State authorities",
    },
  ];

  return <div className="registrar-center">
    <style>{STYLE}</style>
    <header className="registrar-hero"><div><small>REGISTRAR SUBMISSION PACKAGE</small><h2>Complete Digital Project File</h2><p>Every document is collected once, arranged serial-wise and sent through PMC → District → State → DCDC.</p></div><b className={packageReady ? "ready" : "pending"}>{packageReady ? "READY FOR PMC" : `${missingRequired.length} REQUIRED PENDING`}</b></header>
    {message.text && <div className={`registrar-message ${message.type}`}>{message.text}<button onClick={() => setMessage({ type: "", text: "" })}>×</button></div>}
    <section className="registrar-cycle">{lifecycleSteps.map((step, index) => <article className={step.done ? "done" : "pending"} key={step.title}><i>{step.done ? "✓" : index + 1}</i><div><b>{step.title}</b><span>{step.note}</span></div><strong>{step.done ? "COMPLETE" : "PENDING"}</strong></article>)}</section>
    <section className="registrar-card"><div className="registrar-actions"><button onClick={previewApplication}>Preview Bilingual Application</button>{project.registrarPackage?.url && <a href={project.registrarPackage.url} target="_blank" rel="noreferrer">Download Last Saved ZIP</a>}<button className="primary" onClick={generatePackage} disabled={Boolean(busy) || !packageReady}>{busy === "package" ? "Building PDF File ZIP..." : "Generate Complete Registrar ZIP"}</button></div></section>
    <section className="registrar-card">
      <h3>Serial-wise Document Checklist</h3>
      <div className="registrar-list">
        {groupState.map((group, index) => {
          const mergeReady =
            group.id === "RESOLUTIONS" &&
            officialPacsDocuments(project).length === REQUIRED_PACS_DOCUMENT_COUNT;
          const viewable =
            group.record?.url && group.record.url !== "MULTIPLE_RESOLUTIONS";
          const status =
            group.id === "RESOLUTIONS" && group.record?.url === "MULTIPLE_RESOLUTIONS"
              ? "READY TO COMBINE"
              : group.available
                ? "READY"
                : group.optional
                  ? "OPTIONAL"
                  : "PENDING";
          return <article key={group.id} className={group.available ? "available" : "missing"}>
            <i>{String(index + 1).padStart(2, "0")}</i>
            <div>
              <b>{group.title}</b>
              <span>{
                group.available
                  ? group.record?.title || group.record?.fileName || "Automatically linked from project"
                  : group.statusNote || (group.optional ? "Optional / not yet linked" : "Required document pending")
              }</span>
            </div>
            <strong>{status}</strong>
            <div className="record-actions">
              {viewable && <a href={group.record.url} target="_blank" rel="noreferrer">{
                group.id === "PMC_FINAL_DPR"
                  ? "View DPR"
                  : group.id === "SIGNED_MOU"
                    ? "View Signed MOU"
                    : "View PDF"
              }</a>}
              {viewable && group.workflowManaged && <button
                type="button"
                className="download"
                onClick={() => downloadWorkflowDocument(group)}
                disabled={Boolean(busy)}
              >{busy === `download:${group.id}` ? "Downloading..." : group.downloadLabel}</button>}
              {mergeReady && <button
                type="button"
                className="merge"
                onClick={createCombinedOfficialPdf}
                disabled={Boolean(busy)}
              >{busy === "combine" ? "Combining..." : viewable ? "Refresh Combined PDF" : "Create & View Combined PDF"}</button>}
              {group.available && !group.workflowManaged && <button
                type="button"
                className="delete"
                onClick={() => deleteRegistrarDocument(group)}
                disabled={Boolean(busy)}
              >{busy === `delete:${group.id}` ? "Deleting..." : "Delete"}</button>}
            </div>
            {!group.workflowManaged ? <label>Replace / Upload
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                onChange={(event) => setSelectedFiles((old) => ({
                  ...old,
                  [group.id]: event.target.files?.[0] || null,
                }))}
              />
            </label> : <span className="workflow-source">AUTO-LINKED FROM WORKFLOW</span>}
          </article>;
        })}
      </div>
      <footer>
        <span>Registration, bye-laws, financials, audits, NAFED Assurance Letter and other Registrar records can be uploaded here. Final DPR and fully signed MOU are linked automatically from their approved workflows.</span>
        <button onClick={uploadSelected} disabled={Boolean(busy)}>{busy === "upload" ? "Uploading..." : "Upload Selected Documents"}</button>
      </footer>
    </section>
  </div>;
}

const STYLE = `
.registrar-center{display:grid;gap:18px;color:#17233a}.registrar-hero{display:flex;justify-content:space-between;align-items:center;background:linear-gradient(135deg,#071e3d,#0b5d8f);color:white;border-radius:18px;padding:25px 28px}.registrar-hero small{font-weight:900;letter-spacing:.1em;color:#acd8f4}.registrar-hero h2{font-size:29px;margin:6px 0}.registrar-hero p{margin:0;color:#d9ebf5}.registrar-hero>b{border-radius:999px;padding:10px 14px;font-size:11px}.registrar-hero>b.ready{background:#dff8e9;color:#116b3c}.registrar-hero>b.pending{background:#fff0cf;color:#855300}.registrar-message{display:flex;justify-content:space-between;border-radius:12px;padding:13px 16px;font-weight:800}.registrar-message.success{background:#e8f8ef;color:#14723f}.registrar-message.error{background:#fff0ef;color:#a12e24}.registrar-message button{border:0;background:none;font-size:20px}.registrar-cycle{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:9px}.registrar-cycle article{display:grid;grid-template-columns:34px 1fr;gap:8px;align-items:center;padding:12px;border:1px solid #cbd8e5;border-radius:10px;background:#fff}.registrar-cycle article.done{border-top:4px solid #15925a}.registrar-cycle article.pending{border-top:4px solid #d59118}.registrar-cycle i{grid-row:1/3;width:30px;height:30px;display:grid;place-items:center;border-radius:50%;background:#123e68;color:#fff;font-style:normal;font-weight:900}.registrar-cycle b,.registrar-cycle span{display:block}.registrar-cycle span{margin-top:3px;color:#66788c;font-size:9px}.registrar-cycle strong{grid-column:2;font-size:8px}.registrar-card{background:white;border:1px solid #dbe5ee;border-radius:16px;padding:20px}.registrar-card h3{margin:0 0 14px;color:#0a3762}.registrar-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap}.registrar-actions button,.registrar-actions a,.registrar-card footer button{border:1px solid #0b629b;background:white;color:#0b5588;border-radius:10px;padding:10px 14px;font-weight:800;text-decoration:none;cursor:pointer}.registrar-actions button.primary{background:#0875b9;color:white}.registrar-actions button:disabled{opacity:.5;cursor:not-allowed}.registrar-list{display:grid;gap:9px}.registrar-list article{display:grid;grid-template-columns:42px minmax(260px,1fr) auto minmax(150px,auto) minmax(140px,190px);gap:12px;align-items:center;border:1px solid #d9e3ed;border-radius:12px;padding:11px}.registrar-list article.available{border-left:4px solid #15925a}.registrar-list article.missing{border-left:4px solid #d59118}.registrar-list i{font-style:normal;font-weight:900;color:#0d5c92}.registrar-list article>div{display:grid;gap:3px}.registrar-list span{font-size:11px;color:#6a7a8e}.registrar-list strong{font-size:9px;color:#253b55;white-space:nowrap}.registrar-list .record-actions{display:flex;align-items:center;justify-content:flex-end;gap:6px;flex-wrap:wrap}.registrar-list .record-actions a,.registrar-list .record-actions button{border:1px solid #b8cadb;border-radius:7px;background:#fff;color:#0b629b;padding:7px 9px;font-size:9px;font-weight:900;text-decoration:none;cursor:pointer;white-space:nowrap}.registrar-list .record-actions button.merge{border-color:#0b629b;background:#eaf5fc}.registrar-list .record-actions button.download{border-color:#0a6d44;background:#0a6d44;color:#fff}.registrar-list .record-actions button.delete{border-color:#e1b4b0;color:#a12e24;background:#fff6f5}.registrar-list .record-actions button:disabled{opacity:.5;cursor:not-allowed}.registrar-list label{font-size:10px;font-weight:800;color:#566b83}.registrar-list input{display:block;max-width:185px;margin-top:3px}.registrar-list .workflow-source{display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:7px 9px;border:1px solid #b9d9c7;border-radius:8px;background:#eef9f2;color:#137346;font-size:8px;font-weight:900;text-align:center}.registrar-card footer{display:flex;justify-content:space-between;align-items:center;gap:12px;border-top:1px solid #e2e9f0;margin-top:15px;padding-top:15px}.registrar-card footer span{color:#60748a;font-size:10px;max-width:70%}@media(max-width:1180px){.registrar-cycle{grid-template-columns:repeat(2,1fr)}.registrar-list article{grid-template-columns:36px 1fr auto}.registrar-list article>div.record-actions,.registrar-list article>label,.registrar-list article>.workflow-source{grid-column:2/4}}@media(max-width:900px){.registrar-hero,.registrar-card footer{flex-direction:column;align-items:flex-start;gap:14px}.registrar-card footer span{max-width:none}.registrar-list article{grid-template-columns:36px 1fr}.registrar-list article>*:nth-child(n+3){grid-column:2}.registrar-actions>*{width:100%;text-align:center}}@media(max-width:600px){.registrar-cycle{grid-template-columns:1fr}}
`;
